// Centralized analytics event-name registry.
//
// PostHog event names are a permanent contract — once a dashboard, funnel, or
// saved query references an event name, renaming it orphans that asset. Keep
// every event the app emits here so the inventory is greppable and the names
// stay typo-proof.
//
// Convention: `<domain>.<verb_object>` in snake_case. Page-view events use
// `<domain>.page_viewed`. Shared verbs across domains keep the taxonomy
// predictable (e.g., `*.page_viewed`, `*.item_saved`).

export const ANALYTICS_EVENTS = Object.freeze({
  // --- Auth pages ---
  AUTH_LOGIN_VIEWED: 'auth.login_page_viewed',
  AUTH_REGISTER_VIEWED: 'auth.register_page_viewed',

  // --- Dashboard ---
  DASHBOARD_VIEWED: 'dashboard.page_viewed',

  // --- Inventory ---
  INVENTORY_VIEWED: 'inventory.page_viewed',
  INVENTORY_ITEM_DETAILS_VIEWED: 'inventory.item_details_viewed',
  INVENTORY_ASSET_FORM_VIEWED: 'inventory.asset_form_viewed',
  // Existing inventory events (already in use across the codebase — listed here
  // so future call sites prefer the constant over the string literal):
  INVENTORY_SCAN_INITIATED: 'inventory.scan_initiated',
  INVENTORY_SCAN_COMPLETED: 'inventory.scan_completed',
  INVENTORY_SCAN_FORM_POPULATED: 'inventory.scan_form_populated',
  INVENTORY_ITEM_SAVED: 'inventory.item_saved',
  INVENTORY_ITEM_DISCARDED: 'inventory.item_discarded',
  INVENTORY_IMAGE_UPLOADED: 'inventory.image_uploaded',
  INVENTORY_IMAGE_UPLOAD_FAILED: 'inventory.image_upload_failed',

  // --- Spaces / Rooms ---
  SPACES_VIEWED: 'spaces.page_viewed',
  SPACES_ROOM_DETAILS_VIEWED: 'spaces.room_details_viewed',
  SPACES_ROOM_FORM_VIEWED: 'spaces.room_form_viewed',

  // --- Houses ---
  HOUSES_LIST_VIEWED: 'houses.list_viewed',
  HOUSES_DETAILS_VIEWED: 'houses.details_viewed',
  HOUSES_FORM_VIEWED: 'houses.form_viewed',
  // House form funnel — submit funnel + in-form interactions. Pair
  // `*_submit_attempted` with `*_submit_succeeded` / `*_submit_failed`
  // to build a drop-off funnel; breakdown failures by `error_code`.
  HOUSES_FORM_SUBMIT_ATTEMPTED: 'houses.form_submit_attempted',
  HOUSES_FORM_SUBMIT_SUCCEEDED: 'houses.form_submit_succeeded',
  HOUSES_FORM_SUBMIT_FAILED: 'houses.form_submit_failed',
  HOUSES_FORM_CANCELLED: 'houses.form_cancelled',
  HOUSES_FORM_ADDRESS_AUTOFILLED: 'houses.form_address_autofilled',
  HOUSES_FORM_IMAGE_ATTACHED: 'houses.form_image_attached',
  // House list / details interactions — navigation intent signals.
  HOUSES_LIST_HOUSE_CLICKED: 'houses.list_house_clicked',
  HOUSES_LIST_ADD_CLICKED: 'houses.list_add_clicked',
  HOUSES_DETAILS_TAB_CHANGED: 'houses.details_tab_changed',
  HOUSES_DETAILS_EDIT_CLICKED: 'houses.details_edit_clicked',
  HOUSES_DETAILS_ROOM_CLICKED: 'houses.details_room_clicked',
  HOUSES_DETAILS_UTILITY_CLICKED: 'houses.details_utility_clicked',
  HOUSES_DETAILS_MORTGAGE_CLICKED: 'houses.details_mortgage_clicked',

  // --- Utilities ---
  UTILITIES_DETAILS_VIEWED: 'utilities.details_viewed',

  // --- Insurance ---
  INSURANCE_FORM_VIEWED: 'insurance.form_viewed',

  // --- Mortgage ---
  MORTGAGE_FORM_VIEWED: 'mortgage.form_viewed',
  MORTGAGE_DETAILS_VIEWED: 'mortgage.details_viewed',
  MORTGAGE_STATEMENT_FORM_VIEWED: 'mortgage.statement_form_viewed',
  MORTGAGE_AMORTIZATION_VIEWED: 'mortgage.amortization_viewed',

  // --- Maintenance / Tasks ---
  MAINTENANCE_VIEWED: 'maintenance.page_viewed',
  MAINTENANCE_TASK_FORM_VIEWED: 'maintenance.task_form_viewed',

  // --- Documents ---
  DOCUMENTS_VIEWED: 'documents.page_viewed',

  // --- 3D Models ---
  MODELS_3D_VIEWED: 'models_3d.page_viewed',

  // --- Notifications ---
  NOTIFICATIONS_VIEWED: 'notifications.page_viewed',

  // --- Profile ---
  PROFILE_VIEWED: 'profile.page_viewed',
  // Profile settings interactions. Toggle events carry `toggle_state`
  // (the new value after the toggle) so we can see adoption of dark mode
  // / notification preferences.
  PROFILE_DARK_MODE_TOGGLED: 'profile.dark_mode_toggled',
  PROFILE_NOTIFICATIONS_TOGGLED: 'profile.notifications_toggled',
  PROFILE_PRIVACY_CLICKED: 'profile.privacy_clicked',
  PROFILE_SIGN_OUT_CLICKED: 'profile.sign_out_clicked',
  PROFILE_NAME_UPDATED: 'profile.name_updated',

  // --- Account deletion (Apple App Store Guideline 5.1.1(v)) ---
  ACCOUNT_DELETE_CLICKED: 'account.delete_clicked',
  ACCOUNT_DELETE_REQUESTED: 'account.delete_requested',
  ACCOUNT_DELETE_CANCELLED: 'account.delete_cancelled',
});

export default ANALYTICS_EVENTS;
